import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { AuditEntry, AuditKind, AuditQuery } from "@nh/shared";
import { Alert, Badge, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import {
  DataTable,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { listAudit } from "./audit.api";
import { dayEndIso, dayStartIso, formatAuditDateTime, roleLabel, statusVariant } from "./format";
import { AuditEntryDetail } from "./AuditEntryDetail";

export interface MovementFilters {
  q: string;
  outcome: "" | "success" | "error";
  method: "" | "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  kind: "" | AuditKind;
  /** Días `YYYY-MM-DD`, tal cual los da el selector. */
  from: string;
  to: string;
}

export const EMPTY_MOVEMENT_FILTERS: MovementFilters = {
  q: "",
  outcome: "",
  method: "",
  kind: "",
  from: "",
  to: "",
};

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;

export interface AuditMovementsTabProps {
  filters: MovementFilters;
  onFiltersChange: (filters: MovementFilters) => void;
  page: number;
  onPageChange: (page: number) => void;
}

/**
 * Movimientos — la bitácora renglón por renglón, lo más nuevo arriba (el
 * orden lo pone el servidor). Un renglón se abre en el detalle con TODO:
 * la petición, la respuesta, el agente y, si fue el navegador, la pila.
 */
export function AuditMovementsTab({
  filters,
  onFiltersChange,
  page,
  onPageChange,
}: AuditMovementsTabProps) {
  const { t } = useTranslation();
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const from = dayStartIso(filters.from);
  const to = dayEndIso(filters.to);
  const query: AuditQuery = {
    page,
    perPage,
    ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
    ...(filters.outcome ? { outcome: filters.outcome } : {}),
    ...(filters.method ? { method: filters.method } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const entries = useQuery({
    queryKey: ["admin", "audit", "list", query],
    queryFn: () => listAudit(query),
    placeholderData: (previous) => previous,
  });

  const items = entries.data?.items ?? [];
  const total = entries.data?.total ?? 0;
  const filtered = Object.values(filters).some((value) => value !== "");

  function set<K extends keyof MovementFilters>(key: K, value: MovementFilters[K]) {
    onFiltersChange({ ...filters, [key]: value });
  }

  return (
    <div aria-busy={entries.isFetching}>
      <FilterBar
        actions={
          filtered ? (
            <Button variant="ghost" size="sm" onClick={() => onFiltersChange(EMPTY_MOVEMENT_FILTERS)}>
              {t("Limpiar filtros")}
            </Button>
          ) : undefined
        }
      >
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Buscar")}
          <div className="w-64">
            <Input
              value={filters.q}
              onChange={(event) => set("q", event.target.value)}
              placeholder={t("Correo, ruta, IP o acción…")}
              aria-label={t("Buscar en la bitácora")}
            />
          </div>
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Resultado")}
          <div className="w-36">
            <Select
              aria-label={t("Resultado")}
              value={filters.outcome}
              onChange={(event) => set("outcome", event.target.value as MovementFilters["outcome"])}
              placeholder={t("Todos")}
              options={[
                { value: "success", label: t("Éxito") },
                { value: "error", label: t("Error") },
              ]}
            />
          </div>
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Método")}
          <div className="w-32">
            <Select
              aria-label={t("Método")}
              value={filters.method}
              onChange={(event) => set("method", event.target.value as MovementFilters["method"])}
              placeholder={t("Todos")}
              options={METHODS.map((value) => ({ value, label: value }))}
            />
          </div>
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Tipo")}
          <div className="w-36">
            <Select
              aria-label={t("Tipo")}
              value={filters.kind}
              onChange={(event) => set("kind", event.target.value as MovementFilters["kind"])}
              placeholder={t("Todos")}
              options={[
                { value: "request", label: t("Petición") },
                { value: "client", label: t("Navegador") },
              ]}
            />
          </div>
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Desde")}
          <Input
            type="date"
            value={filters.from}
            onChange={(event) => set("from", event.target.value)}
          />
        </label>
        <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
          {t("Hasta")}
          <Input type="date" value={filters.to} onChange={(event) => set("to", event.target.value)} />
        </label>
      </FilterBar>

      {entries.isError ? (
        <Alert variant="error" className="m-4">
          {t("No se pudo cargar la bitácora. Inténtalo otra vez.")}
        </Alert>
      ) : entries.isPending ? (
        <div className="p-5">
          <SkeletonTable rows={5} columns={5} />
        </div>
      ) : items.length === 0 ? (
        <div className="p-4">
          <EmptyState
            title={filtered ? t("Sin resultados") : t("La bitácora está vacía")}
            description={
              filtered
                ? t("Nada coincide con esos filtros.")
                : t("Cada petición que cambie algo, y cada error, queda registrada aquí.")
            }
          />
        </div>
      ) : (
        <DataTable caption={t("Movimientos de la bitácora")}>
          <thead>
            <tr>
              <Th className="w-[16%] whitespace-nowrap">{t("Fecha y hora")}</Th>
              <Th className="w-[22%]">{t("Quién")}</Th>
              <Th>{t("Acción")}</Th>
              <Th align="center" className="w-[9%]">{t("Estado")}</Th>
              <Th align="right" className="w-[14%]">{t("IP")}</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((entry) => {
              const who = entry.actorEmail ?? entry.subjectEmail;
              const role = roleLabel(entry.actorRole);
              return (
                <Tr
                  key={entry.id}
                  tabIndex={0}
                  aria-label={t("Ver el detalle de {{label}}", { label: entry.label })}
                  onClick={() => setSelected(entry)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelected(entry);
                    }
                  }}
                  className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ds-violet-pop"
                >
                  <Td className="whitespace-nowrap text-[13px] text-ds-ink-3">
                    {formatAuditDateTime(entry.at)}
                  </Td>
                  <Td>
                    <p className="truncate font-medium" title={who}>
                      {who ?? t("Anónimo")}
                    </p>
                    {role && <p className="text-[12px] text-ds-ink-3">{t(role)}</p>}
                  </Td>
                  <Td>
                    <p className="font-medium">{entry.label}</p>
                    <p className="break-all font-mono text-[12px] text-ds-ink-3">
                      {entry.method} {entry.path}
                    </p>
                  </Td>
                  <Td align="center">
                    <Badge variant={statusVariant(entry.status)} size="sm">
                      {entry.status}
                    </Badge>
                  </Td>
                  <Td align="right" className="whitespace-nowrap">
                    <p className="font-mono text-[12px]">{entry.ip ?? "—"}</p>
                    {entry.country && (
                      <p className="text-[12px] text-ds-ink-3">{entry.country}</p>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </DataTable>
      )}

      {total > 0 && (
        <Pagination
          page={page}
          perPage={perPage}
          total={total}
          onPageChange={onPageChange}
          onPerPageChange={(n) => {
            setPerPage(n);
            onPageChange(1);
          }}
        />
      )}

      <AuditEntryDetail entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
